import { createSlice } from "@reduxjs/toolkit";
import {
  POSType_Enum,
  SalePaymentMethod_Enum,
  TerminalType_Enum,
  TripOptionType_Enum,
} from "services/sale/common/enums";
import {
  GetCarCategories_Response_Data,
  GetTripPrices_Response,
  Point,
  TripOption,
} from "services/sale/common/types";
import { SaleApi_Point } from "services/sale/map/types";
import { GetTripDetail_Response } from "services/sale/passenger/types";
import { TaxiRequestStep_Enum } from "../types/enums";

interface RequestTaxiModel {
  platformType?: TerminalType_Enum;
  mainOptions: Array<TripOption>;
  tripPrices: GetTripPrices_Response;
  vehicleUserTypeId: number;
  cacheId: string;
  // روش پرداخت قطعی انتخاب شده پس از تایید روش پرداخت در مرحله انتخاب نوع و دسته بندی خودرو
  tripDetail: GetTripDetail_Response;
  selectedPaymentMethod: SalePaymentMethod_Enum;
  unitId?: number;
  pcPoseTypes?: Array<POSType_Enum>;
  phoneNumber?: string;
  newPrice?: number;
  // Categories
  activeCategoryId: number;
  activeCategory: GetCarCategories_Response_Data;
  carCategories: GetCarCategories_Response_Data[];
  // گزینه های سفر
  tempOptions: TripOption[];
  stopMinutes: any;
  // مقصد اضافی
  isExtraDestination: boolean;
  totalExtraDestinations: TripOption[];
  extraDestination: TripOption;
  // ویرایش مقصد های اضافی
  editExtraDestinations: TripOption;

  // اطلاعات مقصد
  destinationPoint?: Point;
  destinationAddress?: string;
  destinationArea?: string;
  destinationLocations: Array<SaleApi_Point>;
  PickupLocationId: number;
  showDestinationOnMap: boolean;
  // اطلاعات مارکر معلق بر روی نقشه
  tempMarker: Point;
  isEditExtraDestination: boolean;
  // زمان باقی مانده برای درخواست تاکسی
  remainRequestTime: number;
  //
  requestStep?: TaxiRequestStep_Enum;
  routePoints?: Point[];
  routeOverview?: string;
  routeKm?: number;
}

const initialState: RequestTaxiModel = {
  tripPrices: null!,
  vehicleUserTypeId: null!,
  // Categories
  activeCategoryId: null!,
  activeCategory: null!,
  carCategories: null!,
  // گزینه های سفر
  mainOptions: null!,
  tempOptions: [],
  stopMinutes: null!,
  isExtraDestination: null!,
  totalExtraDestinations: [],
  extraDestination: null!,
  destinationPoint: null!,
  destinationAddress: null!,
  destinationArea: null!,
  destinationLocations: [],
  PickupLocationId: null!,
  showDestinationOnMap: null!,
  tempMarker: null!,
  editExtraDestinations: null!,
  isEditExtraDestination: null!,
  cacheId: null!,
  selectedPaymentMethod: SalePaymentMethod_Enum.pCPos,
  remainRequestTime: null!,
  tripDetail: null!,
};

const RequestTaxiSlice = createSlice({
  name: "tripOptions",
  initialState,
  reducers: {
    clearData: (state, action) => {
      state = {
        tripPrices: null!,
        vehicleUserTypeId: null!,
        activeCategoryId: null!,
        activeCategory: null!,
        carCategories: null!,
        mainOptions: null!,
        tempOptions: [],
        stopMinutes: null!,
        isExtraDestination: null!,
        totalExtraDestinations: [],
        extraDestination: null!,
        destinationPoint: null!,
        destinationAddress: null!,
        destinationArea: null!,
        destinationLocations: [],
        PickupLocationId: null!,
        showDestinationOnMap: null!,
        tempMarker: null!,
        editExtraDestinations: null!,
        isEditExtraDestination: null!,
        cacheId: null!,
        selectedPaymentMethod: SalePaymentMethod_Enum.pCPos,
        remainRequestTime: null!,
        tripDetail: null!,
        unitId: null!,
        phoneNumber: null!,
        newPrice: null!,
        routePoints: [],
        routeOverview: null!,
        routeKm: null!,
      };
      return state;
    },
    updateCarCategories: (state, action) => {
      state.carCategories = action.payload;

      state.activeCategoryId = action.payload.find(
        (category: any) => category?.isSelected
      )?.id!;

      const activeVehicleUserType = action.payload
        .find((category: any) => category?.isSelected)
        .vehicleUserTypes.find(
          (activeVehicleUserTypes: any) => activeVehicleUserTypes?.isSelected
        );

      state.vehicleUserTypeId = activeVehicleUserType?.id;
    },

    updateActiveCategoryId: (state, action) => {
      const activeCarCategory = state.carCategories?.find(
        (activeId) => activeId.id === action.payload.id
      );

      if (activeCarCategory) {
        state.activeCategoryId = activeCarCategory?.id;
        state.activeCategory = activeCarCategory;
      }
    },

    updateTripPrices: (state, action) => {
      state.tripPrices = action.payload;
      state.cacheId = action.payload.cacheId;
    },

    updateSelectedVehicleUserTypeId: (state, action) => {
      state.vehicleUserTypeId = action.payload;
    },

    updateTripOptions: (state, action) => {
      if (Array.isArray(action.payload) && action.payload.length > 0) {
        state.tempOptions = action.payload;
      } else {
        const selectedOption = state.tempOptions?.find(
          (option) => option?.type === action.payload.type
        );

        if (selectedOption === undefined) {
          state.tempOptions.push(action.payload);
        } else if (selectedOption !== action.payload.value) {
          selectedOption.value = action.payload.value;
        }
      }
    },

    deleteOptions: (state, action) => {
      const selectedOption = state.tempOptions?.filter(
        (item: TripOption) => item.type !== action.payload.type
      );

      state.tempOptions = selectedOption;
    },

    clearTempOptions: (state, action) => {
      state.tempOptions = action.payload;
    },

    updateMainOptions: (state, action) => {
      state.mainOptions = action.payload;
    },

    updateMainDestination: (state, action) => {
      state.destinationAddress = action.payload.destinationAddress;
      state.destinationPoint = action.payload.destinationPoint;
      state.showDestinationOnMap = action.payload.showDestinationOnMap;
    },

    updateTempMarkerInfo: (state, action) => {
      state.tempMarker = action.payload;
    },

    updateIsExtraDestination: (state, action) => {
      state.isExtraDestination = action.payload;
    },

    updateIsEditExtraDestination: (state, action) => {
      state.isEditExtraDestination = action.payload;
    },

    addNewExtraDestination: (state, action) => {
      if (Array.isArray(action.payload) && action.payload.length > 0) {
        state.totalExtraDestinations = action.payload;
      } else {
        state.totalExtraDestinations.push(action.payload);
        state.totalExtraDestinations = state.totalExtraDestinations.sort(
          (a, b) => (a.pointInfo?.priority! > b.pointInfo?.priority! ? 1 : -1)
        );
      }
    },

    updateExtraDestinationPriority: (state, action) => {
      state.extraDestination = action.payload.priority;
    },

    clearExtraDestination: (state, action) => {
      state.totalExtraDestinations = action.payload;
    },

    deleteExtraDestination: (state, action) => {
      const remainingExtraDestinations = state.totalExtraDestinations.filter(
        (extraDest) =>
          extraDest.pointInfo?.priority !== action.payload.pointInfo?.priority
      );

      // اولویت بندی دوباره مقاصد
      remainingExtraDestinations?.forEach((item, inx) => {
        item.pointInfo = { ...item.pointInfo!, priority: inx + 2 };
      });

      state.totalExtraDestinations = remainingExtraDestinations;
      const options = state.tempOptions.filter(
        (options) => options.type !== TripOptionType_Enum.ExtraDestination
      );

      state.tempOptions = options.concat(remainingExtraDestinations);
    },

    editExtraDestinations: (state, action) => {
      state.editExtraDestinations = action.payload;

      state.totalExtraDestinations = state.totalExtraDestinations.filter(
        (extraDest) =>
          extraDest.pointInfo?.priority !== action.payload.pointInfo?.priority
      );

      state.tempOptions = state.tempOptions.filter(
        (options) =>
          options.pointInfo?.priority !== action.payload.pointInfo?.priority
      );
    },

    addTempLocations: (state, action) => {
      const arr1 = state.tempOptions;
      const arr2 = action.payload;

      function getDifference(array1: any, array2: any) {
        return array1.filter(
          (object1: any) =>
            !array2.some(
              (object2: any) =>
                object1.pointInfo?.priority === object2.pointInfo?.priority
            )
        );
      }

      const difference = [...getDifference(arr2, arr1)];

      if (difference.length > 0) {
        state.tempOptions = state.tempOptions.concat(difference);
      }
    },

    updatePayMentMethod: (state, action) => {
      state.selectedPaymentMethod = action.payload;
    },

    updatePhoneNumber: (state, action) => {
      state.phoneNumber = action.payload;
    },
    updatePaymentSetting: (state, action) => {
      state.unitId = action.payload.unitId;
      state.pcPoseTypes = action.payload.pcPoseTypes;
      state.platformType = action.payload.platformType;
    },

    updateNewPrice: (state, action) => {
      state.newPrice = action.payload;
    },

    updateRemainRequestTime: (state, action) => {
      state.remainRequestTime = action.payload;
    },

    updateTripDetail: (state, action) => {
      state.tripDetail = action.payload;
    },

    updateRequestStep: (state, action) => {
      state.requestStep = action.payload;
    },

    updateRouting: (state, action) => {
      state.routePoints = action.payload.routePoints;
      state.routeKm = action.payload.routeKm;
      state.routeOverview = action.payload.routeOverview;
    },
    updateDestinationAddress: (state, action) => {
      state.destinationAddress = action.payload;
    },
  },
});

export default RequestTaxiSlice;
